import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { buildQueryString } from '@/utils';
import { TAG } from '@/constants';
import type {
  BookingRow,
  BookingGeneration,
  BookingListResponse,
  BookingSummaryResponse,
  GetBookingListQuery,
  GetBookingSummaryQuery,
  GetCampaignTimelineQuery,
  CampaignTimelineRow
} from '@/models';

const BOOKING_URL = 'bookings/api/v1';

export interface AwaitingClientOrderResponse {
  campaigns: {
    deal_id: number | null;
    campaign: string | null;
    advertiser: string | null;
    line_items: number;
    gross: number | null;
  }[];
  lineItems: number;
  gross: number;
}

export const bookingApi = createApi({
  reducerPath: 'bookingApi',
  tagTypes: [TAG.BOOKING],
  baseQuery,
  endpoints: (builder) => ({
    getBookingList: builder.query<BookingListResponse, GetBookingListQuery>({
      query: (params) => `${BOOKING_URL}?${buildQueryString(params)}`,
      providesTags: () => [TAG.BOOKING]
    }),
    getBookingSummary: builder.query<BookingSummaryResponse, GetBookingSummaryQuery>({
      query: (params) => `${BOOKING_URL}/summary?${buildQueryString(params)}`,
      providesTags: () => [TAG.BOOKING]
    }),
    getCampaignTimeline: builder.query<CampaignTimelineRow[], GetCampaignTimelineQuery>({
      query: (params) => `${BOOKING_URL}/timeline?${buildQueryString(params)}`,
      providesTags: () => [TAG.BOOKING]
    }),
    /* The line items behind one campaign, fetched when its row is expanded.
       The list endpoint returns campaigns already collapsed, so the lines are
       no longer present in the page payload to expand from. */
    getCampaignLineItems: builder.query<BookingListResponse, GetBookingListQuery>({
      query: (params) => `${BOOKING_URL}?${buildQueryString(params)}`,
      providesTags: () => [TAG.BOOKING]
    }),
    /* Bookings held back pending a signed client order. Read separately
       because every other endpoint here excludes them by design — which is
       why they need reporting at all: otherwise a held booking is only an
       absence, and nobody notices an absence. */
    getAwaitingClientOrder: builder.query<AwaitingClientOrderResponse, void>({
      query: () => `${BOOKING_URL}/awaiting-client-order`,
      providesTags: () => [TAG.BOOKING]
    }),
    /**
     * Amends a booking.
     *
     * Appends a new generation rather than overwriting, so the previous values
     * stay readable — which is why a reason is required rather than optional.
     */
    editBooking: builder.mutation<
      { changed: string[]; generation: string },
      { lineItemId: string; reason: string; changes: Record<string, string | number | null> }
    >({
      query: ({ lineItemId, reason, changes }) => ({
        url: `${BOOKING_URL}/${lineItemId}`,
        method: 'PATCH',
        body: { ...changes, reason }
      }),
      invalidatesTags: () => [TAG.BOOKING]
    }),
    /** Every generation of one line item — the amend log. */
    getBookingHistory: builder.query<{ history: BookingGeneration[] }, string>({
      query: (lineItemId) => `${BOOKING_URL}/${lineItemId}/history`
    })
  })
});

export const {
  useGetBookingListQuery,
  useGetBookingSummaryQuery,
  useGetCampaignTimelineQuery,
  useGetCampaignLineItemsQuery,
  useGetAwaitingClientOrderQuery,
  useEditBookingMutation,
  useLazyGetBookingHistoryQuery,
  util: bookingUtil
} = bookingApi;
